/** @jsxImportSource @emotion/react */
import { StatisticsDateRangePicker } from '../../components/StatisticsDateRangePicker';
import { useStatisticsDateRange } from '../../hooks/useStatisticsDateRange';
import { css } from '@emotion/react';
import { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { Card, message, Spin, Button, Typography, Space } from 'antd';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { AccessSessionTable } from '../components/AccessSessionTable';
import {
  getAccessAvgTimeChartData, getAccessAvgTimeListData, exportAccessAvgTimeExcel,
  AccessListResponse, AccessExportMeta,
} from '../../services/statistics.service';
import { formatNumber } from '../../constants';
import { BAR_LABEL_FONT_SIZE, fitBarLabel } from '../../utils/barLabelLayout';

const { Title } = Typography;

export interface StatisticsAccessAvgTimePageHandle {
  handleExport: () => Promise<void>;
}

interface Props {
  period?: string;
  dateRange?: [string | null, string | null];
  subTabs?: React.ReactNode;
  onExportAvailabilityChange?: (ready: boolean) => void;
}

export const StatisticsAccessAvgTimePage = forwardRef<StatisticsAccessAvgTimePageHandle, Props>(
  ({ period: initialPeriod = 'all', dateRange: initialDateRange = [null, null], subTabs, onExportAvailabilityChange }, ref) => {
    const { period, dateRange, changePeriod: handlePeriodChange,
      applyDateRange } = useStatisticsDateRange(initialPeriod, initialDateRange);
    const chartVersion = useRef(0);
    const listVersion = useRef(0);
    const [chartData, setChartData] = useState<any>(null);
    const [listData, setListData] = useState<AccessListResponse | undefined>();
    const [chartLoading, setChartLoading] = useState(true);
    const [listLoading, setListLoading] = useState(true);
    const [chartFailed, setChartFailed] = useState(false);
    const [listFailed, setListFailed] = useState(false);
    const [, setExportLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const chartRef = useRef<any>(null);

    const fetchChart = useCallback(async () => {
      const version = ++chartVersion.current;
      setChartFailed(false);
      setChartLoading(true);
      try {
        const data = await getAccessAvgTimeChartData({
          period: period !== 'custom' ? period : undefined,
          from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
          to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
        });
        if (version !== chartVersion.current) return;
        setChartData(data);
      } catch {
        if (version !== chartVersion.current) return;
        setChartFailed(true);
        message.error('Không thể tải biểu đồ');
      }
      finally {
        if (version === chartVersion.current) setChartLoading(false);
      }
    }, [period, dateRange]);

    const fetchList = useCallback(async () => {
      const version = ++listVersion.current;
      setListFailed(false);
      setListLoading(true);
      try {
        const res = await getAccessAvgTimeListData({
          period: period !== 'custom' ? period : undefined,
          from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
          to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
          page,
          limit: pageSize,
        });
        if (version !== listVersion.current) return;
        setListData(res);
      } catch {
        if (version !== listVersion.current) return;
        setListFailed(true);
        message.error('Không thể tải danh sách');
      }
      finally {
        if (version === listVersion.current) setListLoading(false);
      }
    }, [period, dateRange, page, pageSize]);

    useEffect(() => {
      onExportAvailabilityChange?.(!chartLoading && !listLoading && !chartFailed && !listFailed);
    }, [chartLoading, listLoading, chartFailed, listFailed, onExportAvailabilityChange]);

    useEffect(() => { setPage(1); }, [period, dateRange]);
    useEffect(() => { fetchChart(); return () => { chartVersion.current += 1; }; }, [fetchChart]);
    useEffect(() => { fetchList(); return () => { listVersion.current += 1; }; }, [fetchList]);

    const handleExport = async () => {
      if (chartLoading || listLoading || chartFailed || listFailed) return;
      setExportLoading(true);
      try {
        let chartImg: string | undefined;
        if (chartRef.current) {
          try {
            chartImg = chartRef.current.getEchartsInstance().getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
          } catch {
            // ignore
          }
        }

        let timeRangeLabel = 'Tất cả';
        if (period === 'today') {
          timeRangeLabel = `${dayjs().format('DD/MM/YYYY')} - ${dayjs().format('DD/MM/YYYY')}`;
        } else if (period === 'this_month') {
          timeRangeLabel = `${dayjs().startOf('month').format('DD/MM/YYYY')} - ${dayjs().endOf('month').format('DD/MM/YYYY')}`;
        } else if (period === 'this_year') {
          timeRangeLabel = `${dayjs().startOf('year').format('DD/MM/YYYY')} - ${dayjs().endOf('year').format('DD/MM/YYYY')}`;
        } else if (period === 'custom' && (dateRange[0] || dateRange[1])) {
          timeRangeLabel = `${dateRange[0] ? dayjs(dateRange[0]).format('DD/MM/YYYY') : '...'} - ${dateRange[1] ? dayjs(dateRange[1]).format('DD/MM/YYYY') : '...'}`;
        }

        const meta: AccessExportMeta = {
          tab_title: 'Lượt truy cập',
          view_by_label: 'Thời gian sử dụng',
          chart_title: 'Thời gian người dùng sử dụng chức năng của hệ thống',
          filters: [],
          time_range_label: timeRangeLabel,
        };

        const blob = await exportAccessAvgTimeExcel({
          period: period !== 'custom' ? period : undefined,
          from_date: period === 'custom' ? (dateRange[0] || undefined) : undefined,
          to_date: period === 'custom' ? (dateRange[1] || undefined) : undefined,
          page: 1,
          limit: 999999,
        }, chartImg, meta);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `thong_ke_thoi_gian_${Date.now()}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch { message.error('Xuất Excel thất bại'); }
      finally { setExportLoading(false); }
    };

    useImperativeHandle(ref, () => ({
      handleExport,
    }));

    const chartOption = useMemo((): any => {
      if (!chartData) return {};
      // 1. Ngắn nhất (Cam), 2. Trung bình (Xanh lá), 3. Dài nhất (Xanh tím) chuẩn Figma
      const COLORS = ['#FA983A', '#66C88F', '#6B82F8'];
      const categories = chartData.categories || [];
      const hasZoom = categories.length > 5;

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any[]) => {
            let html = `<b>${params[0]?.name || ''}</b><br/>`;
            params.forEach((p) => {
              html += `${p.marker} ${p.seriesName}: <b>${formatNumber(p.value)} giây</b><br/>`;
            });
            return html;
          },
        },
        legend: {
          show: false,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: hasZoom ? 55 : 30,
          top: 35,
          containLabel: true,
        },
        dataZoom: hasZoom
          ? [
              {
                type: 'slider',
                show: true,
                startValue: 0,
                endValue: 4, // Hiển thị 5 categories đầu tiên trên màn hình giống Figma
                height: 18,
                bottom: 8,
                borderColor: 'transparent',
                backgroundColor: '#f5f5f5',
                fillerColor: 'rgba(24, 144, 255, 0.2)',
                handleStyle: {
                  color: '#1890ff',
                  borderColor: '#1890ff',
                },
              },
              { type: 'inside', startValue: 0, endValue: 4 },
            ]
          : [],
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: {
            fontSize: 12,
            color: '#333333',
            interval: 0,
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#e8e8e8',
            },
          },
        },
        yAxis: {
          type: 'value',
          name: 'Thời gian (giây)',
          nameTextStyle: {
            color: '#595959',
            fontSize: 12,
            padding: [0, 0, 8, 0],
          },
          minInterval: 1,
          axisLabel: {
            formatter: (val: number) => formatNumber(val),
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#e8e8e8',
            },
          },
        },
        series: (() => {
          return (chartData.series || []).map((s: any, idx: number) => ({
            name: s.name,
            type: 'bar',
            labelLayout: fitBarLabel,
            barWidth: 28,
            barMaxWidth: 36,
            barGap: '15%',
            data: s.data,
            itemStyle: {
              color: COLORS[idx % COLORS.length],
              borderRadius: [2, 2, 0, 0],
            },
            label: {
              show: true,
              position: 'inside',
              overflow: 'truncate',
              ellipsis: '',
              align: 'center',
              verticalAlign: 'middle',
              formatter: (p: any) => {
                const val = Number(p.value) || 0;
                if (val <= 0) return '';
                return formatNumber(val);
              },
              fontSize: BAR_LABEL_FONT_SIZE,
              fontWeight: 500,
              color: '#ffffff',
            },
          }));
        })(),
      };
    }, [chartData]);

    return (
      <div>
        <Card css={chartCardStyle}>
          {/* Sub-tabs (Tổng quan / Người dùng / Tần suất / Thời gian sử dụng) */}
          {subTabs}

          <div css={headerStyle}>
            <Title level={4} style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#000000' }}>
              Thời gian người dùng sử dụng chức năng của hệ thống
            </Title>
            {/* Quick Date Filters on the right */}
            <div css={filterSectionStyle}>
              <Space size="small" wrap>
                <Button
                  type={period === 'today' ? 'primary' : 'text'}
                  onClick={() => handlePeriodChange('today')}
                >
                  Hôm nay
                </Button>
                <Button
                  type={period === 'this_month' ? 'primary' : 'text'}
                  onClick={() => handlePeriodChange('this_month')}
                >
                  Tháng này
                </Button>
                <Button
                  type={period === 'this_year' ? 'primary' : 'text'}
                  onClick={() => handlePeriodChange('this_year')}
                >
                  Năm này
                </Button>
                <Button
                  type={period === 'all' ? 'primary' : 'text'}
                  onClick={() => handlePeriodChange('all')}
                >
                  Tất cả
                </Button>
                <StatisticsDateRangePicker value={dateRange} onChange={applyDateRange}
                  showApply />
              </Space>
            </div>
          </div>

          {/* Chú thích biểu đồ nằm phía trên biểu đồ, tách biệt hoàn toàn khỏi trục tung */}
          <div css={legendRowStyle}>
            <div css={legendItemStyle}>
              <span css={legendBoxStyle('#FA983A')} />
              <span>Thời gian ngắn nhất người dùng sử dụng chức năng</span>
            </div>
            <div css={legendItemStyle}>
              <span css={legendBoxStyle('#66C88F')} />
              <span>Thời gian trung bình người dùng sử dụng chức năng</span>
            </div>
            <div css={legendItemStyle}>
              <span css={legendBoxStyle('#6B82F8')} />
              <span>Thời gian dài nhất người dùng sử dụng chức năng</span>
            </div>
          </div>

          <Spin spinning={chartLoading}>
            {chartData ? (
              <ReactECharts ref={chartRef} option={chartOption} style={{ height: 460 }} notMerge />
            ) : (
              <div css={emptyStyle}>{!chartLoading && 'Không có dữ liệu'}</div>
            )}
          </Spin>
        </Card>

        <AccessSessionTable
          loading={listLoading}
          data={listData}
          page={page}
          pageSize={pageSize}
          onPageChange={(p, ps) => { setPage(p); setPageSize(ps); }}
          showDuration
        />
      </div>
    );
  }
);

const legendRowStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 12px;
`;

const legendItemStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #595959;
`;

const legendBoxStyle = (color: string) => css`
  width: 10px;
  height: 10px;
  background-color: ${color};
  border-radius: 2px;
  display: inline-block;
`;

const emptyStyle = css`
  height: 460px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bfbfbf;
`;

const chartCardStyle = css`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .ant-card-body {
    padding: 16px 20px 24px 20px;
  }
`;

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`;

const filterSectionStyle = css`
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
`;
